import { LogoChoice, LogoPlace } from 'types/adEditorTypes';
import { AdCreativeType } from 'types/adTypes';

/**
 * SYM-5875. Whether the editor should offer a given content field for a template.
 *
 * The renderer declares what it draws and the API sends it alongside the template code. The editor
 * used to decide for itself: salary and benefits were gated on `template === 'b2'` written in the
 * component, and the hook and extra lines were not gated at all -- so it offered a hook line on all
 * six templates when three draw it, and an extra line on all six when one does. Typing into either
 * on S1 did nothing, with nothing on screen to say why.
 *
 * Lives here rather than in the component because the app has no component tests, and the
 * deploy-skew fallback below is precisely the kind of branch that is never exercised by hand.
 */

/**
 * The optional fields the editor can offer, text and colour alike.
 *
 * SYM-5973 / SYM-5974 added the two colours. They are not "content" in the sense the text fields
 * are, but they are governed by exactly the same question -- does this template draw with it --
 * and answering that question twice, in two mechanisms, is how the two pickers came to be the only
 * controls in the panel nobody was gating.
 */
export type ContentField =
  // SYM-6351. The campaign's job title and location, one value per campaign, drawn by the designs
  // that declare them (every design but P0 draws the title; S1, J1, J2, J4, J5 the location).
  | 'job_title'
  | 'location'
  | 'free_text'
  | 'subtext'
  | 'salary'
  | 'benefits'
  | 'banner_title'
  | 'primary_color'
  | 'accent_color';

/**
 * Did this template declare it draws $field?
 *
 * @param declared what the API sent for this template, or undefined/null on an API that predates
 *   the field. That distinction matters: `[]` means "this template draws none of them", while
 *   absent means "nobody told us".
 */
export const templateShowsField = (
  declared: string[] | null | undefined,
  templateCode: string | null | undefined,
  field: ContentField
): boolean => {
  if (declared === undefined || declared === null) {
    // The app and the API deploy separately, so the app can be newer for a while. Hiding every
    // control in that window would take B2's salary and benefits inputs away from customers who
    // can use them -- a worse failure than the one being fixed, and on live customers rather than
    // in an editor nobody has opened yet. Fall back to the rules that applied before.
    // SYM-5915: banner_title is NEW, so there is no "before" behaviour to fall back to. Showing
    // it during the skew window would put a callout control on J1, B2 and D2, which draw no badge
    // -- the do-nothing input SYM-5875 existed to remove. Hidden until the API says otherwise:
    // a missing control for a few minutes beats a control that silently does nothing.
    if (field === 'banner_title') return false;
    // SYM-6351: new like banner_title, and for the same reason hidden until the API declares them.
    if (field === 'job_title' || field === 'location') return false;

    /*
     * SYM-5973 / SYM-5974: the colours fall back the OTHER WAY from banner_title, and the reason
     * is the same reason banner_title falls back to hidden.
     *
     * The rule is "which mistake is cheaper during the few minutes the two repos disagree". A
     * callout control was brand new, so hiding it costs a customer nothing they had yesterday.
     * Both colour pickers already exist and already work on most of the catalogue -- brand on five
     * templates, accent on four -- so hiding them in the skew window would take a working control
     * away from the majority to spare the minority a control that does nothing.
     *
     * Same trade as salary and benefits below, opposite answer to banner_title above.
     */
    if (field === 'primary_color' || field === 'accent_color') return true;

    return field === 'salary' || field === 'benefits'
      ? templateCode === 'b2'
      : true;
  }

  return declared.includes(field);
};

/**
 * Whether the panel has anything to show at all.
 *
 * S1 and J1 draw none of these, so without this the "Template content" heading introduces an empty
 * section -- which reads as a panel that failed to load rather than a template with no options.
 */
export const templateHasAnyField = (
  declared: string[] | null | undefined,
  templateCode: string | null | undefined
): boolean =>
  /*
   * TEXT FIELDS ONLY, and deliberately not ContentField in full.
   *
   * This answers "should the Template content heading be drawn", and the two colour pickers sit
   * OUTSIDE that section with their own labels. SYM-5973 added the colours to ContentField;
   * spreading the whole union here instead would put the heading back on S1 and J1, which draw
   * no text fields at all -- the empty section this function exists to prevent.
   */
  (
    [
      'job_title',
      'location',
      'free_text',
      'subtext',
      'salary',
      'benefits',
      'banner_title',
    ] as ContentField[]
  ).some((field) => templateShowsField(declared, templateCode, field));

/**
 * Can the v2 templates be used for this variant at all? SYM-5860.
 *
 * Two conditions, and only the second was ever checked. SYM-5817 added the first:
 * - PLACEMENT must be story or FEED. Feed used to be excluded because the templates were
 *   1080x1920 only; seven of the eight now render 1080x1350 as well.
 * - CREATIVE TYPE must be image. A story VIDEO passes the placement test, which is why the tiles
 *   stayed on screen when a customer clicked from an image variant to a video one, offering a
 *   choice that could not work.
 *
 * Used for the tiles and for the preview requests, so a video variant does not fire server renders
 * that the server will refuse.
 *
 * ⚠️ THERE IS DELIBERATELY NO CAPABILITY CHECK HERE, for feed any more than for story. A customer
 * without ad-templates-v2 is served no v2 rows at all, so the list this gates is empty for them
 * either way and showsLegacyDefaults() keeps their old overlays on screen. Adding a flag check
 * would be a second copy of an answer the API already gives, and this epic has been bitten four
 * times by exactly that. The CANVAS SHAPE is different and does need the capability — see
 * getCanvasHeight() — because no server response decides it.
 */
export const variantCanUseTemplates = (
  placement: string | null | undefined,
  creativeType: string | null | undefined
): boolean =>
  (placement === 'story' || placement === 'feed') && creativeType === 'image';

/**
 * SYM-5794. Whether the picker still shows the legacy "Default Overlays" section.
 *
 * Mark's curation ends with one coherent set rather than "New Templates" listed above the old
 * overlays: with the flag on, a customer sees the new set and nothing else. But the new set only
 * renders story + image, so hiding the old list unconditionally would leave a VIDEO variant, and
 * every feed variant until SYM-5817 lands, with an empty panel and no way to put an overlay on
 * their ad at all.
 *
 * So the rule is not "flag on, hide it" but "hide it only where the new set can actually serve
 * this variant". `v2Count` is the number of v2 tiles already filtered for this placement and
 * creative type, which is exactly that question answered.
 *
 * The flag is checked as well, even though a customer without it is served no v2 rows: the tiles
 * are wrapped in <PostHogFeature>, so a client-side flag that says false while the API says true
 * would otherwise hide both lists and leave nothing on screen.
 */
export const showsLegacyDefaults = (
  hasTemplatesV2: boolean | null | undefined,
  v2Count: number,
  canTakeOverlay: boolean
): boolean => canTakeOverlay && (!hasTemplatesV2 || v2Count === 0);

/**
 * SYM-5917. Whether an overlay can be applied to this variant at all.
 *
 * The picker offered legacy overlay tiles on a video and clicking one did nothing. Two guards,
 * neither of them on the tile: every placement preview renders <AdEditorVideoContainer/> instead
 * of <AdEditorStencilCanvas/> when the creative is a video, so the fabric canvas is never mounted
 * and the context's `canvas` stays null; and changeOverlay() opens with `if (!canvas) return`.
 *
 * So the tiles were dead controls. SYM-5794 reduced them from six to two, which looked like a
 * lost capability and was nearly escalated to Mark as one. There was no capability: a customer
 * could never put an overlay on an uploaded video. He said so, and he was right.
 *
 * Mirrors the rendering branch EXACTLY -- "not video" rather than "is image" -- because that is
 * the condition the previews use to decide whether a canvas exists, and any other spelling can
 * drift away from it. Deliberately NOT variantCanUseTemplates(), which is placement AND image:
 * legacy overlays do apply to feed images, so that rule would hide a section that works.
 */
export const variantCanTakeOverlay = (
  creativeType: string | null | undefined
): boolean => creativeType !== 'video';

/**
 * SYM-5907. Whether an edit to a length-capped template field may be accepted.
 *
 * The panel used to warn at the limit and let the customer keep typing, because the renderer was
 * believed to ellipsise. It did not: the hook line ran out of the card (SYM-5914) while the
 * message said "This may be shortened in the ad". The cap is now real.
 *
 * Two things stop this from being `next.length <= limit`, and both are the reason it lives here
 * rather than inline in the component, which has no test to catch it:
 *
 * 1. Rows saved before the cap can already exceed it. Trimming them on load would delete a
 *    customer's words for them, so an existing over-long value is left alone.
 * 2. Which means DELETING has to stay possible at any length. A plain cap would freeze an
 *    over-long field completely: every edit, including backspace, produces a value still over the
 *    limit and would be rejected, so the customer could neither keep it nor fix it.
 *
 * Hence: accept anything within the limit, and always accept anything shorter than what is there.
 */
export const acceptsFieldEdit = (
  next: string,
  current: string,
  limit: number
): boolean => next.length <= limit || next.length < current.length;

/**
 * SYM-5928. Whether this variant is displaying a SERVER-rendered v2 template.
 *
 * Mirrors StencilCanvas's own `showsTemplate = Boolean(currentVariant?.template)` EXACTLY, for the
 * same reason variantCanTakeOverlay() mirrors the video branch: it is the condition the preview
 * uses to decide what is on screen, and any other spelling can drift away from it.
 *
 * WHAT THIS GUARDS IS NOT COSMETIC, which is worth stating because the ticket was filed as such.
 *
 * The Fabric canvas is still mounted under a v2 template -- StencilCanvas renders <AdImageCanvas>
 * unconditionally for a non-video variant and paints the server render OVER it in
 * <TemplatePreviewLayer>. So `canvas` is not null, and the drawing tools do not fail:
 *
 *   - addCanvasElement('text') reaches addToCanvas(), where `canvas?.add(element)` SUCCEEDS
 *   - triggerCanvasEvent() then fires 'object:modified'
 *   - the listener on that event runs `updateCurrentVariant('svg', canvas?.toSVG())`
 *
 * The customer sees nothing happen, because the template image covers the canvas. Meanwhile the
 * variant's stored svg has been rewritten with an invisible composite and the ad is marked dirty.
 * "The button does nothing" is what it looks like; what it actually does is write state nobody
 * asked for and nobody can see.
 *
 * Undo is the same shape from the other side: undoChange() calls setCanvasTo(), which returns at
 * `if (!canvas || !currentVariant)` -- but the canvas exists, so it restores a hidden composite
 * instead.
 */
export const variantShowsServerTemplate = (
  template: string | null | undefined
): boolean => Boolean(template);

/**
 * SYM-6227. Whether the toolbar offers the drawing tools -- "Add text" and "Add a rectangle" -- on
 * this variant.
 *
 * They mean "draw something here", and that is true of exactly one thing: a picture ad on the
 * legacy canvas. It is not true of a server-rendered template (SYM-5928, above), and it is not
 * true of a video: a video is not a canvas you draw on, and the text on a Track 1 video comes from
 * the wizard and is drawn by the renderer. On a video ad the canvas is not even mounted
 * (FeedPreview.tsx renders AdEditorVideoContainer instead of the stencil), so the buttons did
 * nothing a person could see.
 *
 * Same treatment the neighbouring buttons got for Track 2 -- SYM-5917 hid the overlay section on
 * video variants, SYM-5860 the templates. This is the last pair.
 */
export const variantShowsDrawingTools = (variant: {
  template?: string | null;
  creative_type?: string | null;
}): boolean =>
  variant.creative_type !== AdCreativeType.VIDEO &&
  !variantShowsServerTemplate(variant.template);

/**
 * SYM-6020. Which overlay a BRAND NEW ad should open with.
 *
 * A new ad is created with no overlay and no template at all, and the editor picks one for it the
 * moment a photo lands. It used to take `defaultAdOverlays[0]` — the first entry of the raw list.
 *
 * ⚠️ THAT LIST IS NOT FILTERED. For a switched-on customer it carries the legacy rows AND the v2
 * rows together, because getDefaultOverlays() filters only on status and adds no ordering. The
 * legacy rows are years older, so index 0 was always a legacy overlay — which that customer can no
 * longer even choose, drawn for a 1000x1000 square on a 1080x1350 canvas. Steven found a new
 * campaign carrying one with "Go to next step" enabled, so it could be published that way.
 *
 * ⭐ THE TWO PATHS ARE DELIBERATELY NOT SYMMETRICAL, and that is Steven's call for this launch.
 *
 * A switched-on customer gets the first NEW overlay for their placement. Everyone else keeps
 * `overlays[0]`, byte for byte, including its lack of a placement filter — because that is what
 * they have today and it is right for them. Narrowing their path too is a separate ticket, kept
 * out of the launch on purpose: a change that only affects customers who asked for nothing is the
 * kind that goes unnoticed until it does not.
 *
 * Returns undefined only when there is nothing to pick, which the caller must treat as "leave the
 * canvas alone" rather than as an error.
 */
export const autoOverlayForNewAd = <
  T extends {
    id?: number;
    placement?: string | null;
    template_code?: string | null;
  }
>(
  overlays: T[],
  placement: string | null | undefined,
  creativeType: string | null | undefined,
  hasTemplatesV2: boolean | undefined
): T | undefined => {
  if (!overlays.length) return undefined;

  if (hasTemplatesV2 && variantCanUseTemplates(placement, creativeType)) {
    // Placement matters here where it did not before: a story template on a feed ad would be the
    // same class of mistake in a new coat.
    return overlays.find((o) => o.template_code && o.placement === placement);
  }

  return overlays[0];
};

/**
 * SYM-6776. Which overlay an ad should carry after the customer chooses a NEW PHOTO.
 *
 * autoOverlayForNewAd() is for an ad WITHOUT a design. The photo update ran it for every ad, so a
 * customer who had picked design B got the first design of the placement back, without a word.
 *
 * An ad keeps its own design when that design is still on offer for this placement and creative
 * type: the same v2 row the picker shows (PlacementMenu.tsx, v2Overlays). Matched on `overlay_id`
 * first, because that is the customer's pick; on `template` second, for an ad that only carries
 * the code. Everything else falls through to the automatic pick, unchanged.
 */
export const overlayForNewPhoto = <
  T extends {
    id?: number;
    placement?: string | null;
    template_code?: string | null;
  }
>(
  overlays: T[],
  current: { overlay_id?: number | null; template?: string | null },
  placement: string | null | undefined,
  creativeType: string | null | undefined,
  hasTemplatesV2: boolean | undefined
): T | undefined => {
  if (hasTemplatesV2 && variantCanUseTemplates(placement, creativeType)) {
    const onOffer = overlays.filter(
      (o) => o.template_code && o.placement === placement
    );
    const kept =
      onOffer.find(
        (o) => current.overlay_id != null && o.id === current.overlay_id
      ) ??
      onOffer.find(
        (o) => current.template && o.template_code === current.template
      );
    if (kept) return kept;
  }

  return autoOverlayForNewAd(overlays, placement, creativeType, hasTemplatesV2);
};

/**
 * SYM-6020, second half. What a brand new ad must NOT inherit from the ad it was copied from.
 *
 * ⚠️ PRESSING + DOES NOT BUILD A FRESH AD. It copies the neighbouring one and blanks a few things
 * -- the picture, the logo, the crop. `template` was never in that list, so the new ad arrived
 * carrying the neighbour's template while having no picture.
 *
 * Six of the seven templates cannot be drawn without one, so the server refused, which is the 400
 * Steven saw. Worse, an ad that HAS a template gets the template layer painted over the canvas, and
 * that layer sits above the "Click here to upload an image/video" prompt -- so the one instruction
 * that would have explained everything was hidden underneath. A customer on the old overlays sees
 * that prompt plainly; a customer on v2 did not, and nobody chose that.
 *
 * ⭐ WHY BLANKING IS RIGHT RATHER THAN RAISING THE PROMPT ABOVE THE LAYER. A brand new ad has no
 * picture, so there is no honest template to show it -- whatever we painted would be a picture of
 * an ad that cannot be made yet. Blanking leaves the ad empty, the prompt visible, and
 * autoOverlayForNewAd() gives it the first real template the moment a picture arrives. That is the
 * behaviour Steven asked for, arrived at from the other end.
 *
 * ⭐ AND IT CHANGES NOTHING FOR CUSTOMERS WHO ARE NOT SWITCHED ON. They have no template on their
 * ads, so blanking a field that is already empty is a no-op for them. Same launch rule as the rest
 * of this work.
 *
 * ⚠️ THIS IS FOR + ONLY, NOT FOR DUPLICATE. duplicateCurrentVariant() copies the whole ad on
 * purpose, picture and template together, and a copy of a finished ad should stay finished.
 */
export const NEW_AD_INHERITS_NOTHING = {
  path: null,
  logo: null,
  svg: null,
  crop: null,
  template: null,
  overlay_id: null,
} as const;

/**
 * SYM-6301. Which logo row to draw for a template, and which button to light.
 *
 * Both answers are the renderer's, sent with the template, for the same reason content_fields is:
 * the editor restating where six templates put their logo would be a seventh copy of six numbers,
 * and it would go stale silently the first time one of them moved.
 */
export type LogoChoiceResolution = {
  row: LogoChoice;
  /** The button lit on an ad nobody has clicked. Null when there is no row. */
  ownPlace: LogoPlace | null;
};

/**
 * @param choice what the API sent, or undefined/null on an API that predates the field.
 */
export const templateLogoChoice = (
  choice: LogoChoice | null | undefined,
  ownPlace: LogoPlace | null | undefined
): LogoChoiceResolution => {
  /*
   * Absent falls back to NO ROW, the same way banner_title falls back to hidden above, and for the
   * same reason: the app and the API deploy separately, so the app can be newer for a few minutes.
   * This row is brand new, so hiding it in that window costs a customer nothing they had
   * yesterday. Showing it instead would put four buttons on every template including the ones that
   * draw no logo at all, and clicking them would do nothing -- the do-nothing control SYM-5875
   * exists to remove.
   */
  if (choice === undefined || choice === null) {
    return { row: 'none', ownPlace: null };
  }

  if (choice === 'none') return { row: 'none', ownPlace: null };

  return {
    row: choice,
    // A server that sends a row but no default is not a case that should exist; falling back to
    // top left keeps one button lit rather than showing a row with nothing selected, which reads
    // as "your choice did not save".
    ownPlace: ownPlace ?? 'top-left',
  };
};
